import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  IconCalendar, 
  IconUser, 
  IconTag, 
  IconSearch, 
  IconArrowRight 
} from './icons';
import { blogPosts } from '../data';

const Blog: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const categories = [
    { id: 'all', label: 'Todos', count: blogPosts.length },
    { id: 'desarrollo web', label: 'Desarrollo Web', count: blogPosts.filter(p => p.category === 'desarrollo web').length },
    { id: 'iOS Dev', label: 'iOS Dev', count: blogPosts.filter(p => p.category === 'iOS Dev').length }
  ];

  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <section id="blog" className="py-20 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Blog <span className="gradient-text">Técnico</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Artículos sobre desarrollo web, iOS, y tecnología
          </p>
        </motion.div>

        {/* Search and Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="max-w-2xl mx-auto">
            {/* Search Bar */}
            <div className="relative mb-8">
              <IconSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar artículos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    selectedCategory === category.id
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.label} ({category.count})
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Blog Posts Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid lg:grid-cols-3 gap-8"
        >
          {filteredPosts.map((post, index) => (
            <motion.article
              key={post.id}
              variants={itemVariants}
              whileHover={{ y: -5 }}
              className="card group cursor-pointer"
            >
              {/* Post Header */}
              <div className="mb-4">
                <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                  <div className="flex items-center">
                    <IconCalendar className="mr-1" size={14} />
                    <span>{formatDate(post.date)}</span>
                  </div>
                  <div className="flex items-center">
                    <IconUser className="mr-1" size={14} />
                    <span>{post.author}</span>
                  </div>
                </div>
                <div className="flex items-center">
                  <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                    {post.category}
                  </span>
                </div>
              </div>

              {/* Post Content */}
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors duration-200">
                {post.title}
              </h3>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                {post.excerpt}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.slice(0, 3).map((tag, tagIndex) => (
                  <span
                    key={tagIndex}
                    className="flex items-center text-xs text-gray-500"
                  >
                    <IconTag className="mr-1" size={10} />
                    {tag}
                  </span>
                ))}
              </div>

              {/* Read More Link */}
              <div className="flex items-center text-primary-600 group-hover:text-primary-700 transition-colors duration-200">
                <span className="text-sm font-medium">Leer más</span>
                    <IconArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform duration-200" size={14} />
                  </div>
            </motion.article>
          ))}
        </motion.div>

        {/* No Results */}
        {filteredPosts.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <p className="text-gray-500 text-lg">
              No se encontraron artículos que coincidan con tu búsqueda.
            </p>
          </motion.div>
        )}

        {/* Blog Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">
                  {blogPosts.length}
                </div>
                <p className="text-gray-700">Artículos publicados</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">
                  {categories.length - 1}
                </div>
                <p className="text-gray-700">Categorías</p>
              </div>
              <div>
                <div className="text-3xl font-bold text-primary-600 mb-2">
                  {Array.from(new Set(blogPosts.flatMap(p => p.tags))).length}
                </div>
                <p className="text-gray-700">Etiquetas únicas</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Blog;